import { itemGroupMembersMarkup } from "../js/query/item-group-presentation.js";
import { titleIdentityMarkup } from "../js/fishydex/title-presentation.js";
import { zoneIdentityMarkup } from "../js/components/zone-identity.js";
import { querySuggestionRowsMarkup } from "../js/query/presentation.js";
import {
  buildQueryInputCatalogLookups,
  fishFilterTermMetadata,
} from "./map-search-state.js";
import {
  parseSearchExpressionDslResult,
  tradeNpcKeyFromSearchTerm,
  tradeNpcSearchTermForKey,
} from "./map-search-contract.js";
import {
  fishIdentityMarkup,
  resolveFishGrade,
} from "../js/components/fish-identity.js";
import { buildItemGroupPresentationModel } from "./map-item-group-presentation.js";
import { normalizeQuerySourceText } from "./map-query-source-text.js";
import {
  specialItemIdentity,
  specialItemReferenceForTerm,
} from "./map-special-item-references.js";
import { tradeNpcPortraitMarkup } from "./map-trade-npc-presentation.js";
import {
  resolveTradeNpcBadgeImageUrl,
  resolveTradeNpcImageUrl,
} from "./map-trade-summary.js";
import { siteText } from "./map-i18n.js";

const QUERY_ZONE_LOOKUP_CACHE = new WeakMap();
const INLINE_ITEM_GROUP_LIMIT = 3;

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function escapeHtml(value) {
  return String(value ?? "").replace(
    /[&<>"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[char] || char,
  );
}

function parseIntegerCsv(value) {
  return String(value || "")
    .split(",")
    .map((part) => Number.parseInt(part, 10))
    .filter((number, index, values) =>
      Number.isInteger(number) && number > 0 && values.indexOf(number) === index,
    );
}

function parseSourceKinds(value) {
  const seen = new Set();
  return String(value || "")
    .split(",")
    .map((part) => part.trim().toLowerCase())
    .filter((sourceKind) => {
      if (!sourceKind || seen.has(sourceKind)) {
        return false;
      }
      seen.add(sourceKind);
      return true;
    })
    .sort();
}

function spriteIcon(name, sizeClass = "size-5") {
  return `<svg class="fishy-icon ${sizeClass}" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-${name}"></use></svg>`;
}

export function fishFilterTermIconMarkup(term, sizeClass = "size-4") {
  const metadata = fishFilterTermMetadata(term);
  return spriteIcon(
    metadata?.icon || "question-mark",
    `${sizeClass} shrink-0 ${metadata?.iconClass || "text-base-content/60"}`.trim(),
  );
}

export function buildItemGroupPresentation(term, itemLookup, special = null) {
  return buildItemGroupPresentationModel(
    term,
    itemLookup,
    special,
    resolveFishGrade,
  );
}

function itemGroupSuggestionMarkup(presentation, itemLookup) {
  if (presentation?.nameGroup) return presentation.nameGroup.contentMarkup;
  const members = Array.isArray(presentation?.directMembers)
    ? presentation.directMembers
    : [];
  const identityMarkup = presentation?.identity
    ? fishIdentityMarkup(presentation.identity, itemLookup)
    : "";
  if (presentation?.isSpecial) {
    return `
      <span class="inline-flex min-w-0 items-center" data-item-group-summary>
        ${identityMarkup}
      </span>
    `;
  }
  const previewMembers = members.slice(0, INLINE_ITEM_GROUP_LIMIT);
  const remainingCount = members.length - previewMembers.length;
  return `
    <span class="inline-flex min-w-0 items-center gap-1.5" data-item-group-summary>
      ${itemGroupMembersMarkup(previewMembers, itemLookup)}
      ${remainingCount > 0
        ? `<span class="badge badge-ghost badge-xs">+${escapeHtml(remainingCount)}</span>`
        : ""}
    </span>
  `;
}

export function tradeNpcIdentityMarkup(npc) {
  const npcKey = Number.parseInt(npc?.npcKey, 10);
  const name = String(npc?.npcName || `NPC ${npcKey || "?"}`).trim();
  const iconUrl = resolveTradeNpcImageUrl(npc);
  const iconMarkup = tradeNpcPortraitMarkup({
    imageUrl: iconUrl,
    badgeImageUrl: resolveTradeNpcBadgeImageUrl(npc),
    name,
    variant: "query",
  });
  return `<span class="fishy-item-row">${iconMarkup}<span class="fishy-domain-label fishy-item-label truncate max-w-40">${escapeHtml(name)}</span></span>`;
}

export function semanticIdentityMarkup(text) {
  return `<span class="truncate max-w-40">${escapeHtml(text)}</span>`;
}

function expressionPreviewTerm(expression) {
  if (!expression || typeof expression !== "object") {
    return null;
  }
  if (expression.type === "term") {
    return expression.term || null;
  }
  if (expression.type === "container") {
    return expressionPreviewTerm(expression.expression);
  }
  if (expression.type === "group" && Array.isArray(expression.children)) {
    if (expression.children.length === 1) {
      return expressionPreviewTerm(expression.children[0]);
    }
    const patchTerms = expression.children
      .map(expressionPreviewTerm)
      .filter((term) => term?.kind === "patch-bound");
    if (patchTerms.length) {
      const fromPatch = patchTerms.find((term) => term.bound === "from")?.patchId || "";
      const toPatch = patchTerms.find((term) => term.bound === "to")?.patchId || "";
      if (fromPatch && toPatch && fromPatch === toPatch) {
        return { kind: "patch-window", patchId: fromPatch };
      }
      return { kind: "patch-window", fromPatchId: fromPatch, toPatchId: toPatch };
    }
  }
  return null;
}

function parseDraftPreviewTerm(rawSource, catalog, options = {}) {
  const parsed = parseSearchExpressionDslResult(rawSource, { catalog });
  const allowImplicitOr = options.allowImplicitOr !== false;
  const hasError = (parsed.issues || []).some((issue) =>
    issue?.severity !== "warning" || (!allowImplicitOr && issue?.code === "implicit_or")
  );
  return !hasError ? expressionPreviewTerm(parsed.expression) : null;
}

function sourceBadgesMarkup(sourceKinds) {
  const kinds = Array.isArray(sourceKinds) ? sourceKinds : [];
  if (!kinds.length) {
    return "";
  }
  return `<span class="fishymap-query-token-sources" aria-hidden="true">${kinds.map((kind) => `<span>${escapeHtml(kind)}</span>`).join("")}</span>`;
}

function queryTokenPreviewMarkup(innerHtml) {
  const markup = normalizeQuerySourceText(innerHtml).trim();
  return `<span class="fishymap-query-token-preview" aria-hidden="true">${markup}</span>`;
}

function titleCaseQueryText(value) {
  return String(value ?? "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function layerFilterLayerLabel(layerId) {
  const normalized = String(layerId ?? "").trim().toLowerCase();
  if (normalized === "zones" || normalized === "zone_mask") {
    return "Fishing zones";
  }
  if (normalized === "hotspots") {
    return "Hotspots";
  }
  if (normalized === "bookmarks") {
    return "Bookmarks";
  }
  return titleCaseQueryText(normalized || "Layer");
}

function layerFilterGroupLabel(group) {
  const normalized = String(group ?? "").trim();
  if (normalized === "high_quality") {
    return "High Quality";
  }
  return titleCaseQueryText(normalized);
}

function layerFilterPillMarkup(text, tone = "neutral") {
  return `<span class="fishymap-layer-filter-pill tone-${escapeHtml(tone)}">${escapeHtml(text)}</span>`;
}

function layerFilterSubjectLabel(subject, lookups) {
  if (subject?.kind === "fish") {
    return lookups.fish.get(subject.fishId)?.name || `Fish ${subject.fishId}`;
  }
  if (subject?.kind === "item") {
    const item = lookups.items.get(subject.itemId);
    return item?.name || `Item ${subject.itemId}`;
  }
  if (subject?.kind === "item-group") {
    const count = Array.isArray(subject.itemIds) ? subject.itemIds.length : 0;
    return count === 1 ? `Item ${subject.itemIds[0]}` : `${count} items`;
  }
  if (subject?.kind === "zone") {
    const zoneRgb = Number.parseInt(subject.zoneRgb, 10);
    const zone = lookups.zones?.get(zoneRgb);
    return zone?.name || `Zone 0x${zoneRgb.toString(16).padStart(6, "0")}`;
  }
  return titleCaseQueryText(subject?.kind || "Subject");
}

function layerFilterSubjectMarkup(subject, lookups) {
  if (subject?.kind === "fish") {
    const fish = lookups.fish.get(subject.fishId);
    return fishIdentityMarkup(
      { ...(fish || {}), fishId: subject.fishId, name: fish?.name || `Fish ${subject.fishId}` },
      lookups.items,
    );
  }
  if (subject?.kind === "item") {
    const item = lookups.items.get(subject.itemId);
    return fishIdentityMarkup(
      { ...(item || {}), itemId: subject.itemId, name: item?.name || `Item ${subject.itemId}` },
      lookups.items,
    );
  }
  if (subject?.kind === "item-group") {
    const count = Array.isArray(subject.itemIds) ? subject.itemIds.length : 0;
    return layerFilterPillMarkup(count === 1 ? `Item ${subject.itemIds[0]}` : `${count} items`, "subject");
  }
  if (subject?.kind === "zone") {
    const zoneRgb = Number.parseInt(subject.zoneRgb, 10);
    const zone = lookups.zones?.get(zoneRgb);
    return zoneIdentityMarkup(zone || { zoneRgb, name: `Zone 0x${zoneRgb.toString(16).padStart(6, "0")}` });
  }
  return layerFilterPillMarkup(layerFilterSubjectLabel(subject, lookups), "subject");
}

function layerFilterPercentLabel(value) {
  const basisPoints = Number.parseInt(value?.basisPoints ?? value?.basis_points, 10);
  if (!Number.isInteger(basisPoints)) {
    return "";
  }
  const whole = Math.trunc(basisPoints / 100);
  const fraction = basisPoints % 100;
  return fraction === 0
    ? `${whole}%`
    : `${whole}.${String(fraction).padStart(2, "0").replace(/0+$/, "")}%`;
}

function layerFilterCompareLabel(op) {
  return {
    eq: "=",
    "eq-ci": "~=",
    ne: "!=",
    gt: ">",
    gte: ">=",
    lt: "<",
    lte: "<=",
  }[String(op ?? "")] || "=";
}

function layerFilterPredicateLabel(predicate, lookups) {
  if (!predicate || typeof predicate !== "object") {
    return "";
  }
  if (predicate.kind === "bool") {
    const op = String(predicate.op || "or").toUpperCase();
    const children = (Array.isArray(predicate.children) ? predicate.children : [])
      .map((child) => layerFilterPredicateLabel(child, lookups))
      .filter(Boolean);
    return children.length > 2 ? `${children.length} conditions` : children.join(` ${op} `);
  }
  if (predicate.kind === "subject-presence") {
    return layerFilterSubjectLabel(predicate.subject, lookups);
  }
  if (predicate.kind === "membership") {
    const subject = layerFilterSubjectLabel(predicate.subject, lookups);
    const group = titleCaseQueryText(predicate.group);
    const op = predicate.op === "not-in" ? "not in" : "in";
    const rate = predicate.rate
      ? ` ${layerFilterCompareLabel(predicate.rate.op)} ${layerFilterPercentLabel(predicate.rate.value)}`
      : "";
    return `${subject} ${op} ${group}${rate}`;
  }
  if (predicate.kind === "group-rate") {
    return `${titleCaseQueryText(predicate.group)} ${layerFilterCompareLabel(predicate.comparison?.op)} ${layerFilterPercentLabel(predicate.comparison?.value)}`;
  }
  if (predicate.kind === "property-compare") {
    const value = predicate.value?.kind === "percent"
      ? layerFilterPercentLabel(predicate.value)
      : String(predicate.value?.value ?? "");
    return `${titleCaseQueryText(predicate.field)} ${layerFilterCompareLabel(predicate.op)} ${value}`;
  }
  if (predicate.kind === "relation") {
    return `${titleCaseQueryText(predicate.relation)} ${layerFilterSubjectLabel(predicate.target, lookups)}`;
  }
  return "";
}

function layerFilterPredicateMarkup(predicate, lookups) {
  if (!predicate || typeof predicate !== "object") {
    return "";
  }
  if (predicate.kind === "bool") {
    const op = String(predicate.op || "or").toUpperCase();
    const children = (Array.isArray(predicate.children) ? predicate.children : [])
      .map((child) => layerFilterPredicateMarkup(child, lookups))
      .filter(Boolean);
    return children.map((child, index) =>
      `${index > 0 ? layerFilterPillMarkup(op, "operator") : ""}${child}`
    ).join("");
  }
  if (predicate.kind === "subject-presence") {
    return `<span class="fishymap-layer-filter-predicate">${layerFilterSubjectMarkup(predicate.subject, lookups)}</span>`;
  }
  if (predicate.kind === "membership") {
    const subject = layerFilterSubjectMarkup(predicate.subject, lookups);
    const group = layerFilterPillMarkup(layerFilterGroupLabel(predicate.group), "group");
    const op = layerFilterPillMarkup(predicate.op === "not-in" ? "NOT IN" : "IN", "operator");
    const rate = predicate.rate
      ? `${layerFilterPillMarkup(layerFilterCompareLabel(predicate.rate.op), "operator")}${layerFilterPillMarkup(layerFilterPercentLabel(predicate.rate.value), "value")}`
      : "";
    return `<span class="fishymap-layer-filter-predicate">${subject}${op}${group}${rate}</span>`;
  }
  if (predicate.kind === "group-rate") {
    return `<span class="fishymap-layer-filter-predicate">${layerFilterPillMarkup(layerFilterGroupLabel(predicate.group), "group")}${layerFilterPillMarkup(layerFilterCompareLabel(predicate.comparison?.op), "operator")}${layerFilterPillMarkup(layerFilterPercentLabel(predicate.comparison?.value), "value")}</span>`;
  }
  if (predicate.kind === "property-compare") {
    const value = predicate.value?.kind === "percent"
      ? layerFilterPercentLabel(predicate.value)
      : String(predicate.value?.value ?? "");
    return `<span class="fishymap-layer-filter-predicate">${layerFilterPillMarkup(titleCaseQueryText(predicate.field), "field")}${layerFilterPillMarkup(layerFilterCompareLabel(predicate.op), "operator")}${layerFilterPillMarkup(value, "value")}</span>`;
  }
  if (predicate.kind === "relation") {
    return `<span class="fishymap-layer-filter-predicate">${layerFilterPillMarkup(titleCaseQueryText(predicate.relation), "relation")}${layerFilterSubjectMarkup(predicate.target, lookups)}</span>`;
  }
  return "";
}

function layerFactPreviewMarkup(term) {
  const conditions = Array.isArray(term.conditions) ? term.conditions : [];
  const conditionMarkup = conditions.length
    ? `<span class="fishymap-layer-filter-predicate">${conditions.map((condition) => {
        const op = condition.op === "exists" ? "has" : layerFilterCompareLabel(condition.op);
        const value = condition.op === "exists" ? "" : ` ${String(condition.value ?? "")}`;
        return layerFilterPillMarkup(`${condition.path} ${op}${value}`.trim(), "value");
      }).join("")}</span>`
    : "";
  return `
    <span class="fishymap-layer-filter-preview">
      ${spriteIcon("filter-2-fill", "size-3.5 text-base-content/60")}
      ${layerFilterPillMarkup(layerFilterLayerLabel(term.layerId), "layer")}
      ${layerFilterPillMarkup(term.factId || "fact", "field")}
      ${conditionMarkup}
    </span>
  `;
}

function draftTermPreviewMarkup(term, lookups) {
  if (!term || typeof term !== "object") {
    return "";
  }
  if (term.kind === "fishing-contest") {
    return queryTokenPreviewMarkup(`${spriteIcon("medal-fill", "size-4")} ${escapeHtml(siteText("contest.query_title"))}`);
  }
  if (term.kind === "status" || term.kind === "grade") {
    const value = term.kind === "status" ? term.status : term.grade;
    const metadata = fishFilterTermMetadata(value);
    return queryTokenPreviewMarkup(`${fishFilterTermIconMarkup(value, "size-3.5")}<span>${escapeHtml(metadata?.label || value)}</span>`);
  }
  if (term.kind === "title") {
    const title = lookups.titles?.find(title => title.titleId === term.titleId);
    return title ? queryTokenPreviewMarkup(titleIdentityMarkup(title)) : "";
  }
  if (term.kind === "fish") {
    const fish = lookups.fish.get(term.fishId);
    const name = fish?.name || `Fish ${term.fishId}`;
    return queryTokenPreviewMarkup(
      fishIdentityMarkup({ ...(fish || {}), fishId: term.fishId, name }, lookups.items),
    );
  }
  if (term.kind === "item") {
    const special = specialItemReferenceForTerm(term, lookups.specialItemReferences);
    const item = lookups.items.get(term.itemId);
    const identity = special
      ? specialItemIdentity(special, item || { itemId: term.itemId })
      : { ...(item || {}), itemId: term.itemId, name: item?.name || `Item ${term.itemId}` };
    return queryTokenPreviewMarkup(
      `${fishIdentityMarkup(identity, lookups.items)}${sourceBadgesMarkup(term.sourceKinds)}`,
    );
  }
  if (term.kind === "item-group") {
    const special = specialItemReferenceForTerm(term, lookups.specialItemReferences);
    const presentation = buildItemGroupPresentation(term, lookups.items, special);
    return queryTokenPreviewMarkup(
      `${itemGroupSuggestionMarkup(presentation, lookups.items)}${sourceBadgesMarkup(term.sourceKinds || special?.sourceKinds)}`,
    );
  }
  if (term.kind === "patch-window") {
    const label = term.patchId || [term.fromPatchId, term.toPatchId].filter(Boolean).join(" -> ");
    return queryTokenPreviewMarkup(`${spriteIcon("calendar-2-fill", "size-3.5 text-base-content/60")}<span>${escapeHtml(label || "Patch")}</span>`);
  }
  if (term.kind === "patch-bound") {
    return queryTokenPreviewMarkup(`${spriteIcon("calendar-2-fill", "size-3.5 text-base-content/60")}<span>${escapeHtml(`${term.bound}:${term.patchId || ""}`)}</span>`);
  }
  if (term.kind === "zone") {
    const zoneRgb = Number(term.zoneRgb);
    const hex = Number.isFinite(zoneRgb) ? `0x${zoneRgb.toString(16).padStart(6, "0")}` : "";
    const zone = lookups.zones?.get(Number.parseInt(term.zoneRgb, 10));
    const displayZone = zone || (Number.isFinite(zoneRgb) ? { zoneRgb, name: hex } : null);
    return queryTokenPreviewMarkup(zoneIdentityMarkup(displayZone));
  }
  if (term.kind === "semantic") {
    const key = `${term.layerId}:${term.fieldId}`;
    const semantic = lookups.semantic.get(key);
    return queryTokenPreviewMarkup(`${spriteIcon("map-2-fill", "size-3.5 text-base-content/60")}<span>${escapeHtml(semantic?.label || key)}</span>`);
  }
  if (term.kind === "layer-filter") {
    const layerLabel = titleCaseQueryText(term.layerId);
    const predicateMarkup = layerFilterPredicateMarkup(term.predicate, lookups);
    const predicateLabel = layerFilterPredicateLabel(term.predicate, lookups);
    return queryTokenPreviewMarkup(`
      <span class="fishymap-layer-filter-preview">
        ${spriteIcon("filter-2-fill", "size-3.5 text-base-content/60")}
        ${layerFilterPillMarkup(layerFilterLayerLabel(term.layerId) || layerLabel, "layer")}
        ${predicateMarkup || `<span>${escapeHtml(predicateLabel || "filter")}</span>`}
      </span>
    `);
  }
  if (term.kind === "layer-fact") {
    const npcKey = tradeNpcKeyFromSearchTerm(term);
    if (npcKey != null) {
      const npc = lookups.tradeNpcs?.get(npcKey);
      return queryTokenPreviewMarkup(tradeNpcIdentityMarkup({
        ...(npc || {}),
        npcKey,
        npcName: npc?.npcName || `NPC ${npcKey}`,
      }));
    }
    return queryTokenPreviewMarkup(layerFactPreviewMarkup(term));
  }
  return "";
}

function previewLookupsFromBundle(bundle) {
  const { catalog, lookups } = buildQueryInputCatalogLookups(bundle);
  return {
    catalog,
    lookups: {
      titles: catalog.titles,
      fish: lookups.fish,
      items: lookups.items,
      zones: zoneLookupByRgb(catalog.zones),
      semantic: lookups.semantic,
      tradeNpcs: lookups.tradeNpcs,
      specialItemReferences: lookups.specialItemReferences,
    },
  };
}

function querySuggestionCatalogTerm(suggestion) {
  const kind = String(suggestion?.catalogKind || "").trim();
  const entry = isPlainObject(suggestion?.catalogEntry) ? suggestion.catalogEntry : null;
  if (!entry) {
    return null;
  }
  if (kind === "filter") {
    if (entry.kind === "fishing-contest") {
      return { kind: "fishing-contest" };
    }
    const status = String(entry.status || "").trim();
    const grade = String(entry.grade || "").trim();
    return status
      ? { kind: "status", status }
      : grade
        ? { kind: "grade", grade }
        : null;
  }
  if (kind === "title") return { kind: "title", titleId: entry.titleId };
  if (kind === "fish") {
    const fishId = Number.parseInt(entry.itemId ?? entry.fishId, 10);
    return Number.isInteger(fishId) && fishId > 0 ? { kind: "fish", fishId } : null;
  }
  if (kind === "item") {
    const itemId = Number.parseInt(entry.itemId, 10);
    return Number.isInteger(itemId) && itemId > 0
      ? { kind: "item", itemId, sourceKinds: parseSourceKinds(entry.sourceKinds) }
      : null;
  }
  if (kind === "item-group") {
    const itemIds = parseIntegerCsv(Array.isArray(entry.itemIds) ? entry.itemIds.join(",") : entry.itemIds);
    return itemIds.length
      ? { kind: "item-group", itemIds, sourceKinds: parseSourceKinds(entry.sourceKinds) }
      : null;
  }
  if (kind === "zone") {
    const zoneRgb = Number.parseInt(entry.zoneRgb, 10);
    return Number.isInteger(zoneRgb) && zoneRgb > 0 ? { kind: "zone", zoneRgb } : null;
  }
  if (kind === "semantic") {
    const layerId = String(entry.layerId || "").trim();
    const fieldId = Number.parseInt(entry.fieldId, 10);
    return layerId && Number.isInteger(fieldId) ? { kind: "semantic", layerId, fieldId } : null;
  }
  if (kind === "patch") {
    const bound = String(entry.bound || "").trim();
    const patchId = String(entry.patchId || "").trim();
    return bound ? { kind: "patch-bound", bound, patchId } : null;
  }
  if (kind === "trade-npc") {
    const npcKey = Number.parseInt(entry.npcKey, 10);
    return Number.isInteger(npcKey) && npcKey > 0
      ? tradeNpcSearchTermForKey(npcKey)
      : null;
  }
  return null;
}

function querySuggestionStructuralTerm(suggestion) {
  const kind = String(suggestion?.kind || "").trim();
  const insertText = String(suggestion?.insertText || "").trim();
  if (kind === "status" && insertText.startsWith("status:")) {
    const status = insertText.slice("status:".length).trim();
    return status ? { kind: "status", status } : null;
  }
  if (kind === "grade" && insertText.startsWith("grade:")) {
    const grade = insertText.slice("grade:".length).trim();
    return grade ? { kind: "grade", grade } : null;
  }
  return null;
}

function querySuggestionTerm(suggestion, catalog) {
  return querySuggestionCatalogTerm(suggestion)
    || querySuggestionStructuralTerm(suggestion)
    || (["prefix", "operator", "source-kind"].includes(String(suggestion?.kind || "").trim())
      ? null
      : parseDraftPreviewTerm(suggestion?.previewText || suggestion?.insertText, catalog));
}

function querySuggestionKindLabel(suggestion, term) {
  if (term?.kind === "status" || term?.kind === "grade" || term?.kind === "fishing-contest") {
    return "Filter";
  }
  if (term?.kind === "title") return siteText("fishydex.titles.title_column");
  if (term?.kind === "fish") {
    return "Fish";
  }
  if (term?.kind === "item" || term?.kind === "item-group") {
    return "Item";
  }
  if (term?.kind === "zone") {
    return "Zone";
  }
  if (term?.kind === "patch-bound" || term?.kind === "patch-window") {
    return "Patch";
  }
  if (term?.kind === "semantic") {
    return "Map";
  }
  if (term?.kind === "layer-filter") {
    return "Layer";
  }
  if (term?.kind === "layer-fact") {
    return tradeNpcKeyFromSearchTerm(term) != null ? "NPC" : "Fact";
  }

  switch (String(suggestion?.kind || "").trim()) {
    case "prefix":
      return "Prefix";
    case "operator":
      return "Operator";
    case "source-kind":
      return "Source";
    case "layer-filter-layer":
    case "layer-filter-subject":
    case "layer-filter-group":
    case "layer-filter-rate":
    case "layer-filter-field":
    case "layer-filter-relation":
    case "layer-filter-snippet":
      return "Layer";
    case "layer-fact-layer":
    case "layer-fact":
      return "Fact";
    default:
      return "";
  }
}

function highlightedMatchedText(value, matchLike) {
  const text = String(value ?? "");
  const matchedValue = String(matchLike?.matchedValue ?? "");
  if (!text || !matchedValue || text.trim().toLowerCase() !== matchedValue.trim().toLowerCase()) {
    return escapeHtml(text);
  }
  const windowStart = Number.parseInt(matchLike?.windowStart, 10);
  const windowEnd = Number.parseInt(matchLike?.windowEnd, 10);
  if (!Number.isInteger(windowStart) || !Number.isInteger(windowEnd) || windowEnd <= windowStart) {
    return escapeHtml(text);
  }
  const tokens = Array.from(text.matchAll(/\p{L}+|\p{N}+/gu)).map((match) => ({
    start: match.index,
    end: match.index + match[0].length,
  }));
  if (!tokens[windowStart] || !tokens[windowEnd - 1]) {
    return escapeHtml(text);
  }
  const start = tokens[windowStart].start;
  const end = tokens[windowEnd - 1].end;
  return [
    escapeHtml(text.slice(0, start)),
    `<mark class="fishymap-query-match-highlight">${escapeHtml(text.slice(start, end))}</mark>`,
    escapeHtml(text.slice(end)),
  ].join("");
}

function querySuggestionPrimaryMarkup(suggestion, term, lookups) {
  if (term) {
    const preview = draftTermPreviewMarkup(term, lookups);
    if (preview) {
      return preview;
    }
  }
  return `<span class="block truncate font-semibold">${highlightedMatchedText(suggestion.label, suggestion)}</span>`;
}

function querySuggestionCatalogAttributes(suggestion) {
  const catalogKind = String(suggestion?.catalogKind || "").trim();
  const entry = isPlainObject(suggestion?.catalogEntry) ? suggestion.catalogEntry : {};
  const attributes = [];
  if (catalogKind) {
    attributes.push(["data-catalog-kind", catalogKind]);
  }
  const fishId = Number.parseInt(entry.fishId, 10);
  if (Number.isInteger(fishId) && fishId > 0) {
    attributes.push(["data-fish-id", fishId]);
  }
  const itemId = Number.parseInt(entry.itemId, 10);
  if (Number.isInteger(itemId) && itemId > 0) {
    attributes.push(["data-item-id", itemId]);
  }
  const itemIds = parseIntegerCsv(Array.isArray(entry.itemIds) ? entry.itemIds.join(",") : entry.itemIds);
  if (itemIds.length) {
    attributes.push(["data-item-ids", itemIds.join(",")]);
  }
  const sourceKinds = parseSourceKinds(Array.isArray(entry.sourceKinds) ? entry.sourceKinds.join(",") : entry.sourceKinds);
  if (sourceKinds.length) {
    attributes.push(["data-source-kinds", sourceKinds.join(",")]);
  }
  const zoneRgb = Number.parseInt(entry.zoneRgb, 10);
  if (Number.isInteger(zoneRgb) && zoneRgb > 0) {
    attributes.push(["data-zone-rgb", zoneRgb]);
  }
  const layerId = String(entry.layerId ?? "").trim();
  const fieldId = Number.parseInt(entry.fieldId, 10);
  if (layerId) {
    attributes.push(["data-semantic-layer-id", layerId]);
  }
  if (Number.isInteger(fieldId)) {
    attributes.push(["data-semantic-field-id", fieldId]);
  }
  const semanticLabel = String(entry.label || "").trim();
  if (layerId && semanticLabel) {
    attributes.push(["data-semantic-label", semanticLabel]);
  }
  const patchId = String(entry.patchId ?? "").trim();
  if (patchId) {
    attributes.push(["data-patch-id", patchId]);
  }
  const patchBound = String(entry.bound || "").trim();
  if (patchBound) {
    attributes.push(["data-patch-bound", patchBound]);
  }
  const npcKey = Number.parseInt(entry.npcKey, 10);
  if (Number.isInteger(npcKey) && npcKey > 0) {
    attributes.push(["data-npc-key", npcKey]);
  }
  return attributes
    .map(([name, value]) => `${name}="${escapeHtml(value)}"`)
    .join(" ");
}

export function buildQuerySuggestionRowsHtml(suggestions, bundle, highlightedSuggestionIndex = -1) {
  const { catalog, lookups } = previewLookupsFromBundle(bundle);
  return querySuggestionRowsMarkup((Array.isArray(suggestions) ? suggestions : []).map(suggestion => {
    const term = querySuggestionTerm(suggestion, catalog);
    return {
      primaryMarkup: querySuggestionPrimaryMarkup(suggestion, term, lookups),
      kindLabel: querySuggestionKindLabel(suggestion, term),
      attributes: querySuggestionCatalogAttributes(suggestion),
      insertText: suggestion.insertText,
    };
  }), highlightedSuggestionIndex);
}

function zoneLookupByRgb(zones) {
  const catalog = Array.isArray(zones) ? zones : [];
  const cached = QUERY_ZONE_LOOKUP_CACHE.get(catalog);
  if (cached?.length === catalog.length) {
    return cached.value;
  }
  const value = new Map(
    catalog
      .map((zone) => [Number.parseInt(zone?.zoneRgb, 10), zone])
      .filter(([zoneRgb]) => Number.isInteger(zoneRgb) && zoneRgb > 0),
  );
  QUERY_ZONE_LOOKUP_CACHE.set(catalog, { length: catalog.length, value });
  return value;
}

export function querySuggestionListKey(suggestions) {
  return JSON.stringify(
    (Array.isArray(suggestions) ? suggestions : []).map((suggestion) => [
      suggestion.kind,
      suggestion.insertText,
      suggestion.replaceStart,
      suggestion.replaceEnd,
      suggestion.catalogKind,
      querySuggestionCatalogAttributes(suggestion),
      suggestion.matchMode,
      suggestion.matchedValue,
      suggestion.windowStart,
      suggestion.windowEnd,
    ]),
  );
}
